/* TurnWizard admin — the shell.
 *
 * Owns the top bar, the gate, and which of the three screens is showing. It
 * knows nothing about what any page does; pages register themselves with nav.js
 * and are rendered here by `<component :is>`, the same arrangement the console's
 * shell has with its modules.
 *
 * The one piece of real logic is the watcher that keeps `store.selectedId` in
 * step with the URL. It has to be here rather than in a page, because a page
 * cannot mount for a tenant that has not been selected yet — and the URL is
 * where a tenant comes from when somebody pastes a link.
 */
import * as Vue from 'vue';
import { api } from './api.js';
import { nav } from './nav.js';
import { store } from './store.js';
import { SignIn } from './components/sign-in.js';
import { Sidebar } from './components/sidebar.js';
import { TenantList } from './components/tenant-list.js';
import { Admins } from './pages/admins.js';
import { Plans } from './pages/plans.js';

var App = {
  name: 'TurnWizardAdmin',
  components: {
    SignIn: SignIn,
    Sidebar: Sidebar,
    TenantList: TenantList,
    Admins: Admins,
    Plans: Plans
  },
  setup: function () {
    nav.apply();

    var ready = Vue.computed(function () {
      return !api.auth.checking && api.auth.user && api.auth.isAdmin;
    });

    /* The URL is the source of truth for which salon is open.
     *
     * Both directions matter. Following a link straight to #/tenants/<id>/board
     * has to select that tenant and load its tables; and a page that is already
     * open when the hash changes has to follow. One watcher does both because
     * they are the same event.
     *
     * The tenant LIST is loaded too, and not only for the list screen: the
     * sidebar and every page header read the salon's name out of it. Arriving
     * directly at a page would otherwise show "Loading…" for ever.
     */
    Vue.watch(function () { return [ready.value, nav.route.tenantId]; }, function () {
      if (!ready.value) return;
      if (!store.listLoaded.value) store.loadTenants();
      if (!store.support.loaded) store.loadSupport();
      if (nav.route.tenantId && nav.route.tenantId !== store.selectedId.value) {
        store.select(nav.route.tenantId);
      }
    }, { immediate: true });

    // "Saving…" / "Saved" / the last error. One line for the whole app — see the
    // note on `status` in api.js for why it is not per control.
    var statusLabel = Vue.computed(function () {
      if (api.status.busy > 0) return 'Working…';
      if (api.status.error) return 'Failed';
      if (api.status.savedAt) return 'Saved';
      return '';
    });

    /* Signing out with a support session live would leave the row behind, and
     * the row is what makes the admin's console another salon's console. It
     * expires on its own, but "on its own" is up to eight hours, and the next
     * thing somebody does after signing out of this app is close the laptop.
     *
     * Stopped first, and the sign-out happens either way — a failed stop must
     * not trap somebody in a session they are trying to leave. */
    async function signOut() {
      if (store.supporting.value) await store.stopSupport();
      await api.signOut();
    }

    return {
      auth: api.auth,
      status: api.status,
      statusLabel: statusLabel,
      ready: ready,
      route: nav.route,
      active: nav.active,
      signOut: signOut,
      goList: nav.goList,
      goAdmins: nav.goAdmins,
      goPlans: nav.goPlans,
      goTenant: nav.goTenant,
      selected: store.selected,
      supporting: store.supporting,
      supportLeft: store.supportLeft,
      stopSupport: store.stopSupport
    };
  },
  template: [
    '<div v-if="auth.checking" class="gate"><div class="gate__card">',
    '  <h1 class="gate__brand">TurnWizard <span>Admin</span></h1>',
    '  <p class="gate__lead">Checking this session…</p>',
    '</div></div>',
    '',
    '<sign-in v-else-if="!ready" />',
    '',
    '<template v-else>',
    '  <header class="topbar">',
    '    <div class="topbar__inner">',
    '      <button type="button" class="topbar__brand" @click="goList()">',
    '        TurnWizard <span>Admin</span></button>',
    '',
    '      <nav class="topbar__nav">',
    '        <button type="button" class="topbar__link"',
    '          :class="{ \'is-on\': route.screen === \'tenants\' || route.screen === \'tenant\' }"',
    '          @click="goList()">Tenants</button>',
    '        <button type="button" class="topbar__link" :class="{ \'is-on\': route.screen === \'plans\' }"',
    '          @click="goPlans()">Plans</button>',
    '        <button type="button" class="topbar__link" :class="{ \'is-on\': route.screen === \'admins\' }"',
    '          @click="goAdmins()">Admins</button>',
    '      </nav>',
    '',
    '      <span class="topbar__status" :class="{ \'is-busy\': status.busy > 0, \'is-bad\': status.error }"',
    '        :title="status.error || \'\'">{{ statusLabel }}</span>',
    '',
    '      <span class="topbar__who">{{ auth.user.email }}</span>',
    '      <button type="button" class="btn btn--ghost btn--sm" @click="signOut">Sign out</button>',
    '    </div>',
    '',
    /* On every screen, not only the salon it belongs to. A support session makes
       the admin's console another business's console, and the failure mode is
       forgetting — so it follows them around until it is stopped or runs out. */
    '    <div v-if="supporting" class="support-bar" :class="{ \'is-urgent\': supportLeft.urgent }">',
    '      <span class="support-bar__dot" aria-hidden="true"></span>',
    '      <span>Your <strong>console</strong> is signed in as',
    '        <button type="button" class="support-bar__name"',
    '          @click="goTenant(supporting.tenantId)">{{ supporting.tenantName }}</button>',
    '        &mdash; {{ supportLeft.label }} left</span>',
    '      <button type="button" class="btn btn--sm support-bar__stop" @click="stopSupport">',
    '        End session</button>',
    '    </div>',
    '  </header>',
    '',
    '  <main class="main">',
    '    <admins v-if="route.screen === \'admins\'" />',
    '    <plans v-else-if="route.screen === \'plans\'" />',
    '',
    '    <div v-else-if="route.screen === \'tenant\'" class="layout">',
    '      <sidebar />',
    '      <div class="layout__body">',
    '        <component v-if="active" :is="active.page" :key="route.tenantId + \':\' + active.id" />',
    '        <p v-else class="empty">No page is registered under that name.</p>',
    '      </div>',
    '    </div>',
    '',
    '    <tenant-list v-else />',
    '  </main>',
    '</template>',
    '',
    '<transition name="toast">',
    '  <div v-if="status.toast" class="toast" :class="\'toast--\' + status.toast.kind">',
    '    {{ status.toast.text }}</div>',
    '</transition>'
  ].join('\n')
};

export { App };
